/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * In-memory localStorage backing store plus CDU consent detection and migration
 * helpers used by the localStorage consent wrapper.
 */

import { InMemoryStorage, getConsentStatus as getSharedConsentStatus, snapshotStorage } from './in-memory-storage';

/** Reference to the real localStorage, captured before the wrapper replaces it. */
export const originalLocalStorage = window.localStorage;

let inMemoryStorage: InMemoryStorage | null = null;

/**
 * Get in memory storage.
 */
export function getInMemoryStorage(): InMemoryStorage {
  if (!inMemoryStorage) {
    inMemoryStorage = new InMemoryStorage();
  }
  return inMemoryStorage;
}

/**
 * Gets consent status from in-memory storage or cookies.
 *
 * @returns {string} Consent status: 'pending', 'accepted', or 'rejected'
 */
function getConsentStatus(): 'pending' | 'accepted' | 'rejected' {
  return getSharedConsentStatus(() => getInMemoryStorage().getItem('cdu_consent_v1'));
}

/**
 * @returns `true` when CDU consent has been explicitly accepted.
 */
export function hasConsent(): boolean {
  return getConsentStatus() === 'accepted';
}

/**
 * Migrates data from in-memory storage to real localStorage when consent is accepted.
 *
 * @returns {void}
 */
export function migrateInMemoryToLocalStorage(): void {
  console.log('[LocalStorageWrapper] Migrating in-memory data to localStorage');

  const memory = getInMemoryStorage();

  const keys: string[] = [];
  for (let i = 0; i < memory.length; i++) {
    const key = memory.key(i);
    if (key) {
      keys.push(key);
    }
  }

  keys.forEach((key) => {
    try {
      const value = memory.getItem(key);
      if (value !== null) {
        originalLocalStorage.setItem(key, value);
        console.log(`[LocalStorageWrapper] Migrated key: ${key}`);
      }
    } catch (error) {
      console.error(`[LocalStorageWrapper] Error migrating key ${key}:`, error);
    }
  });

  memory.clear();
  console.log('[LocalStorageWrapper] In-memory data migration complete');
}

/**
 * Discards all buffered in-memory localStorage entries (e.g. when consent is
 * rejected).
 */
export function clearInMemoryStorage(): void {
  getInMemoryStorage().clear();
  console.log('[LocalStorageWrapper] In-memory storage cleared');
}

/**
 * Gets all keys and values from in-memory localStorage (debugging).
 *
 * @returns {Record<string, string>} Object with all in-memory localStorage data
 */
export function getInMemoryStorageContents(): Record<string, string> {
  return snapshotStorage(getInMemoryStorage());
}
